import { Card } from './Card.js';
import { Suit, Rank, CardData } from '../types/index.js';
import { Solver } from './Solver.js';

export interface DealResult {
  tableau: Card[][];
  stock: Card[];
}

export interface DealOptions {
  solvableOnly: boolean;
  drawMode: number;
  maxAttempts: number;
}

export class Deck {
  /**
   * Create a standard 52-card deck
   */
  static createStandardDeck(): Card[] {
    const cards: Card[] = [];
    const suits = [Suit.SPADES, Suit.HEARTS, Suit.DIAMONDS, Suit.CLUBS];
    const ranks = [
      Rank.ACE, Rank.TWO, Rank.THREE, Rank.FOUR, Rank.FIVE, Rank.SIX,
      Rank.SEVEN, Rank.EIGHT, Rank.NINE, Rank.TEN, Rank.JACK, Rank.QUEEN, Rank.KING
    ];

    for (const suit of suits) {
      for (const rank of ranks) {
        cards.push(new Card(suit, rank, false));
      }
    }

    return cards;
  }

  /**
   * Shuffle cards using Fisher-Yates algorithm
   */
  static shuffle(cards: Card[]): Card[] {
    const shuffled = [...cards];

    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    return shuffled;
  }

  /**
   * Deal cards for Klondike Solitaire
   * 7 tableau piles with 1-7 cards each (top card face-up)
   * Remaining 24 cards go to stock (all face-down)
   */
  static dealKlondike(options?: Partial<DealOptions>): DealResult {
    const opts: DealOptions = {
      solvableOnly: options?.solvableOnly ?? false,
      drawMode: options?.drawMode ?? 3,
      maxAttempts: options?.maxAttempts ?? 100
    };

    let attempts = 0;

    while (attempts < opts.maxAttempts) {
      attempts++;

      const deck = Deck.shuffle(Deck.createStandardDeck());
      const tableau: Card[][] = [[], [], [], [], [], [], []];
      let cardIndex = 0;

      // Deal to tableau (7 piles with increasing number of cards)
      for (let pileIndex = 0; pileIndex < 7; pileIndex++) {
        for (let cardNum = 0; cardNum <= pileIndex; cardNum++) {
          const card = deck[cardIndex++];

          // Only the top card (last one dealt to each pile) is face-up
          if (cardNum === pileIndex) {
            card.flip();
          }

          tableau[pileIndex].push(card);
        }
      }

      // Remaining cards go to stock (all face-down)
      const stock = deck.slice(cardIndex);

      // If solvable only mode, check if solvable
      if (opts.solvableOnly) {
        const isSolvable = Solver.isSolvable(
          stock,
          [],
          [[], [], [], []],
          tableau,
          opts.drawMode
        );

        if (isSolvable) {
          console.log(`Found solvable game on attempt ${attempts}`);
          return { tableau, stock };
        }
      } else {
        // Random mode - return immediately
        return { tableau, stock };
      }
    }

    // Fallback: return the last attempt even if not solvable
    console.warn(`Could not find solvable game after ${opts.maxAttempts} attempts. Returning last attempt.`);
    const deck = Deck.shuffle(Deck.createStandardDeck());
    const tableau: Card[][] = [[], [], [], [], [], [], []];
    let cardIndex = 0;

    for (let pileIndex = 0; pileIndex < 7; pileIndex++) {
      for (let cardNum = 0; cardNum <= pileIndex; cardNum++) {
        const card = deck[cardIndex++];
        if (cardNum === pileIndex) {
          card.flip();
        }
        tableau[pileIndex].push(card);
      }
    }

    const stock = deck.slice(cardIndex);
    return { tableau, stock };
  }

  /**
   * Convert array of Cards to CardData for serialization
   */
  static cardsToData(cards: Card[]): CardData[] {
    return cards.map(card => card.toData());
  }

  /**
   * Convert array of CardData to Cards for deserialization
   */
  static dataToCards(data: CardData[]): Card[] {
    return data.map(cardData => Card.fromData(cardData));
  }
}
